
import React, { useState, useEffect } from 'react';
import { FaRegBuilding } from "react-icons/fa";
import { LuMapPin } from "react-icons/lu";
import { IoMdClose } from "react-icons/io";
import {  FiBriefcase  } from "react-icons/fi";
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import FileUpload from '../../../components/ui/FileUpload';
import PhoneInput from '../../../components/ui/PhoneInput';
import { validateCompanyField, validateAllFields } from '../../../utils/companyValidation';
import { apiCall } from '../../../api/fetchClient';
import { ADD_COMPANY_URL, UPDATE_COMPANY_DETAILS_URL } from '../../../api/url';
import { strings } from '../../../strings/strings';
import { toast } from 'react-toastify';

const CompanyModal = ({ isOpen, onClose, onSave, company }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    companyCode: '',
    companyEmail: '',
    mobileCode: '+91',
    mobileNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: '',
    zipCode: '',
    companyWebsite: '',
    companyLogo: '',
    industry: '',
    companySize: '',
    gstNo: '',
  });

  const [initialFormData, setInitialFormData] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (company) {
      const initialData = {
        companyName: company.companyName || '',
        companyCode: company.accessCompanyCode || company.companyCode || '',
        companyEmail: company.companyEmail || '',
        mobileCode: company.mobileCode || '+91',
        mobileNumber: company.mobileNumber || '',
        addressLine1: company.companyAddress?.addressLine1 || '',
        addressLine2: company.companyAddress?.addressLine2 || '',
        city: company.companyAddress?.city || '',
        state: company.companyAddress?.state || '',
        country: company.companyAddress?.country || '',
        zipCode: company.companyAddress?.zipCode || '',
        companyWebsite: company.companyWebsite || '',
        companyLogo: company.companyLogo || '',
        industry: company.industry || '',
        companySize: company.companySize || '',
        gstNo: company.gstNo || '',
      };
      setFormData(initialData);
      setInitialFormData(initialData);
    } else {
      const newData = {
        companyName: '',
        companyCode: '',
        companyEmail: '',
        mobileCode: '+91',
        mobileNumber: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        country: '',
        zipCode: '',
        companyWebsite: '',
        companyLogo: '',
        industry: '',
        companySize: '',
        gstNo: '',
      };
      setFormData(newData);
      setInitialFormData(null);
    }
    setFormErrors({});
  }, [company, isOpen]);

  const handleMobileCodeChange = (value) => {
    setFormData(prev => ({ 
      ...prev, 
      mobileCode: value,
      mobileNumber: '' 
    }));
    setFormErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors.mobileCode;
      delete newErrors.mobileNumber;
      return newErrors;
    });
  };

  const handleMobileNumberChange = (value) => {
    setFormData(prev => ({ ...prev, mobileNumber: value }));
    
    // Clear mobile number error first
    if (formErrors.mobileNumber) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.mobileNumber;
        return newErrors;
      });
    }
  };

  const handleMobileNumberBlur = (value) => {
    if (value && formData.mobileCode) {
      const error = validateCompanyField('mobileNumber', value, formData.mobileCode);
      if (error) {
        setFormErrors(prev => ({ ...prev, mobileNumber: error }));
      } else {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.mobileNumber;
          return newErrors;
        });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === 'companyCode' ? value.toUpperCase() : value;
    
    setFormData(prev => ({ ...prev, [name]: finalValue }));

    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateCompanyField(
      name, 
      value, 
      name === 'mobileNumber' ? formData.mobileCode : null
    );
    
    if (error) {
      setFormErrors(prev => ({ ...prev, [name]: error }));
    } else {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateAllFields(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }

    try {
      setIsSubmitting(true);

      const dataToSend = new FormData();

      if (company && initialFormData) {
        let hasChanges = false;
        Object.keys(formData).forEach(key => {
          if (formData[key] !== initialFormData[key]) {
            if (key === 'companyLogo') {
              if (formData[key] instanceof Blob || formData[key] instanceof File) {
                dataToSend.append(key, formData[key], 'logo.jpg');
                hasChanges = true;
              }
            } else {
              const payloadKey = key === 'companyCode' ? 'accessCompanyCode' : key;
              dataToSend.append(payloadKey, formData[key]);
              hasChanges = true;
            }
          }
        });

        if (!hasChanges) {
          toast.error(strings.NO_CHANGES_DETECTED);
          setIsSubmitting(false);
          return;
        }
      } else {

        Object.keys(formData).forEach(key => {
          const value = formData[key];
          if (value !== '' && value !== null && value !== undefined) {
            if (key === 'companyLogo') {
              if (value instanceof Blob || value instanceof File) {
                dataToSend.append(key, value, 'logo.jpg');
              }
            } else {
              const payloadKey = key === 'companyCode' ? 'accessCompanyCode' : key;
              dataToSend.append(payloadKey, value);
            }
          }
        });
      }

      let response;
      if (company) {
        response = await apiCall(`${UPDATE_COMPANY_DETAILS_URL}${company._id}`, 'PUT', dataToSend, true);
      } else {
        response = await apiCall(ADD_COMPANY_URL, 'POST', dataToSend, true);
      }

      onSave(response?.results || formData);
      onClose();
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const companySizes = [
    '1-10',
    '11-50',
    '51-200',
    '201-500',
    '501-1000',
    '1000+'
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="relative bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full"></div>
                <div className="relative p-2 sm:p-3 bg-gradient-to-br from-primary to-primary-dark rounded-lg sm:rounded-xl shadow-lg">
                  <FaRegBuilding className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                  {company ? strings.EDIT_COMPANY : strings.ADD_NEW_COMPANY}
                </h2>
                <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm mt-0.5 hidden sm:block">
                  {company ? strings.UPDATE_COMPANY_INFO : strings.COMPLETE_FORM_REGISTER}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex-shrink-0"
            >
              <IoMdClose className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1" noValidate>
          <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
            {/* Basic Information */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-blue-100 dark:border-blue-800/30">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
                <div className="p-1.5 sm:p-2 bg-blue-500 rounded-lg">
                  <FaRegBuilding className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{strings.BASIC_INFORMATION}</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  <Input
                    id="companyName"
                    name="companyName"
                    label={strings.COMPANY_NAME_LABEL}
                    value={formData.companyName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={strings.COMPANYNAME_PLACEHOLDER}
                    error={formErrors.companyName}
                    required
                  />
                  <Input
                    id="companyCode"
                    name="companyCode"
                    label={strings.COMPANY_CODE_LABEL}
                    value={formData.companyCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={strings.COMPANY_CODE_PLACEHOLDER}
                    error={formErrors.companyCode}
                    required
                    disabled={!!company}
                  />
                </div>
                <Input
                  id="companyEmail"
                  name="companyEmail"
                  label={strings.COMPANY_EMAIL_LABEL}
                  type="email"
                  value={formData.companyEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={strings.COMPANEEEMAIL_PLACEHOLDER}
                  error={formErrors.companyEmail}
                  required
                />
                <div>
                  <PhoneInput
                    mobileCodeValue={formData.mobileCode}
                    mobileNumberValue={formData.mobileNumber}
                    onMobileCodeChange={handleMobileCodeChange}
                    onMobileNumberChange={handleMobileNumberChange}
                    onMobileNumberBlur={handleMobileNumberBlur}
                    mobileCodeError={formErrors.mobileCode}
                    mobileNumberError={formErrors.mobileNumber}
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/10 dark:to-emerald-900/10 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-green-100 dark:border-green-800/30">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
                <div className="p-1.5 sm:p-2 bg-green-500 rounded-lg">
                  <LuMapPin className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  {strings.ADDRESS_INFORMATION}
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div className="sm:col-span-2">
                  <Input
                    id="addressLine1"
                    name="addressLine1"
                    label={strings.ADDRESS_LINE_1_LABEL}
                    value={formData.addressLine1}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={strings.ADDRESS_PLACEHOLDER}
                    error={formErrors.addressLine1}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Input
                    id="addressLine2"
                    name="addressLine2"
                    label={strings.ADDRESS_LINE_2_LABEL}
                    value={formData.addressLine2}
                    onChange={handleChange}
                    placeholder={strings.ADDRESS_PLACEHOLDER}
                    error={formErrors.addressLine2}
                  />
                </div>
                <Input
                  id="city"
                  name="city"
                  label={strings.CITY_LABEL}
                  value={formData.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={strings.CITY_PLACEHOLDER}
                  error={formErrors.city}
                />
                <Input
                  id="state"
                  name="state"
                  label={strings.STATE_LABEL}
                  value={formData.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={strings.STATE_PLACEHOLDER}
                  error={formErrors.state}
                />
                <Input
                  id="country"
                  name="country"
                  label={strings.COUNTRY_LABEL}
                  value={formData.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={strings.COUNTRY_PLACEHOLDER}
                  error={formErrors.country}
                />
                <Input
                  id="zipCode"
                  name="zipCode"
                  label={strings.ZIP_CODE_LABEL}
                  type="tel"
                  value={formData.zipCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={strings.ZIPCODE_PLACEHOLDER}
                  maxLength={6}
                  error={formErrors.zipCode}
                />
              </div>
            </div>

            {/* Company Details */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-purple-100 dark:border-purple-800/30">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
                <div className="p-1.5 sm:p-2 bg-purple-500 rounded-lg">
                  <FiBriefcase className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">{strings.COMPANY_DETAILS}</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                <div className="sm:col-span-2">
                  <FileUpload
                    label={strings.COMPANY_LOGO_LABEL}
                    name="companyLogo"
                    value={formData.companyLogo}
                    onChange={handleChange}
                    error={formErrors.companyLogo}
                  />
                </div>
                <Input
                  id="companyWebsite"
                  name="companyWebsite"
                  label={strings.COMPANY_WEBSITE_LABEL}
                  type="url"
                  value={formData.companyWebsite}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={strings.COMPANY_WEBSITE_PLACEHOLDER}
                  error={formErrors.companyWebsite}
                />
                <Input
                  id="industry"
                  name="industry"
                  label={strings.INDUSTRY_LABEL}
                  value={formData.industry}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={strings.INDUSTRY_PLACEHOLDER}
                  error={formErrors.industry}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {strings.COMPANY_SIZE_LABEL}
                  </label>
                  <select
                    name="companySize"
                    value={formData.companySize}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`text-xs sm:text-sm w-full px-4 py-2 rounded-lg border ${formErrors.companySize ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} bg-gray-50 dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 outline-none`}
                  >
                    <option value="">{strings.SELECT_COMPANY_SIZE}</option>
                    {companySizes.map((size) => (
                      <option key={size} value={size}>
                        {size} {strings.EMPLOYEES_SUFFIX}
                      </option>
                    ))}
                  </select>
                  {formErrors.companySize && (
                    <p className="mt-1 text-xs text-red-500">{formErrors.companySize}</p>
                  )}
                </div>
                <Input
                  id="gstNo"
                  name="gstNo"
                  label={strings.GST_NUMBER_LABEL}
                  value={formData.gstNo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder={strings.GST_PLACEHOLDER}
                  error={formErrors.gstNo}
                />
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 sm:gap-4 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
            <Button type="submit" variant="primary" className=" w-max text-[11px] sm:text-sm">
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {company ? strings.UPDATING : strings.ADDING}
                </span>
              ) : company ? (
                strings.UPDATE_COMPANY
              ) : (
          strings.ADD_COMPANY
              )}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose} className="text-[11px] sm:text-sm  w-max">
              {strings.CANCEL}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyModal;